import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { SwissLogo } from "@/components/ui/swiss-logo";
import { Badge } from "@/components/ui/badge";

interface MenuItem {
  icon: string;
  label: string;
  path: string;
}

export const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const normalizedPath = location.pathname.replace(/\/+$/, '') || '/';

  const matchesPath = (target: string) => {
    if (target === '/dashboard') {
      return normalizedPath === '/dashboard';
    }
    return normalizedPath === target || normalizedPath.startsWith(`${target}/`);
  };

  const isOperational = [
    '/operational',
    '/file-processor',
    '/transactions',
    '/review-queue',
    '/validation',
    '/exports'
  ].some(matchesPath);

  const handleViewSwitch = (path: string) => {
    navigate(path);
  };

  // Different menu items for each view - ACTION-ORIENTED LANGUAGE
  const executiveMenuItems: MenuItem[] = [
    { icon: "fa-graduation-cap", label: "Get Started", path: "/onboarding" },
    { icon: "fa-users", label: "Manage Clients", path: "/client-portfolio" },
    { icon: "fa-people-group", label: "Manage Team", path: "/team-management" },
    { icon: "fa-lightbulb", label: "Business Insights", path: "/strategic-intel" },
    { icon: "fa-file-invoice-dollar", label: "Document Status", path: "/file-monitor" },
    { icon: "fa-list-ol", label: "Account Settings", path: "/chart-of-accounts" },
    { icon: "fa-chart-bar", label: "View Reports", path: "/reports" },
    { icon: "fa-credit-card", label: "PayPal Test", path: "/paypal-test" },
    { icon: "fa-gear", label: "Settings", path: "/settings" },
  ];

  const operationalMenuItems: MenuItem[] = [
    { icon: "fa-cloud-arrow-up", label: "Upload Documents", path: "/file-processor" },
    { icon: "fa-right-left", label: "Review Transactions", path: "/transactions" },
    { icon: "fa-robot", label: "Review AI Suggestions", path: "/review-queue" },
    { icon: "fa-triangle-exclamation", label: "Fix Issues", path: "/validation" },
    { icon: "fa-book", label: "View Bookings", path: "/journal-entries" },
    { icon: "fa-percent", label: "VAT Report", path: "/vat-report" },
    { icon: "fa-money-bill-wave", label: "Track Payments", path: "/open-items" },
    { icon: "fa-file-export", label: "Export to Accounting", path: "/exports" },
    { icon: "fa-list-ol", label: "Account Settings", path: "/chart-of-accounts" },
    { icon: "fa-graduation-cap", label: "Teach AI Patterns", path: "/learning-rules" },
    { icon: "fa-microchip", label: "AI Insights", path: "/operational/ai-intelligence" },
    { icon: "fa-gear", label: "Settings", path: "/settings" },
  ];

  const currentMenuItems = isOperational ? operationalMenuItems : executiveMenuItems;

  return (
    <aside className="w-[250px] bg-brand-secondary p-4 flex flex-col h-screen sticky top-0">
      <div className="px-2 pt-[6px] pb-[10px] flex items-center gap-3">
        <SwissLogo size="md" />
        <span className="font-space-grotesk text-xl font-bold text-brand-primary">Amplio</span>
      </div>

      <nav className="flex-grow mt-5 flex flex-col gap-2">
        {/* View Switcher Section */}
        <div className="mb-2 space-y-2">
          {/* NEW: Workspace (Task-First Dashboard) */}
          <button 
            onClick={() => handleViewSwitch('/workspace')}
            aria-current={matchesPath('/workspace') ? 'page' : undefined}
            className={`w-full flex flex-col gap-1 p-3 rounded-xl text-left cursor-pointer transition-all ${
              matchesPath('/workspace')
                ? 'bg-brand-accent text-brand-text-primary shadow-md ring-2 ring-brand-accent/30' 
                : 'text-brand-text-secondary hover:bg-white/50 border border-transparent hover:border-brand-border'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <i className="fa-solid fa-list-check text-base"></i>
                <span className="font-semibold text-sm">Your Workspace</span>
              </div>
              <Badge 
                variant="outline" 
                className={`text-[10px] h-5 bg-brand-success/20 border-brand-success/30 text-brand-success`}
              >
                NEW
              </Badge>
            </div>
            <p className="text-[11px] opacity-70 pl-6">See what needs your attention today</p>
          </button>
          
          <button 
            onClick={() => handleViewSwitch('/dashboard')}
            aria-current={!isOperational && !matchesPath('/workspace') ? 'page' : undefined}
            className={`w-full flex flex-col gap-1 p-3 rounded-xl text-left cursor-pointer transition-all ${
              !isOperational && !matchesPath('/workspace')
                ? 'bg-brand-accent text-brand-text-primary shadow-md ring-2 ring-brand-accent/30' 
                : 'text-brand-text-secondary hover:bg-white/50 border border-transparent hover:border-brand-border'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <i className="fa-solid fa-chart-pie text-base"></i>
                <span className="font-semibold text-sm">Executive Dashboard</span>
              </div>
              <Badge 
                variant="outline" 
                className={`text-[10px] h-5 ${!isOperational && !matchesPath('/workspace') ? 'bg-white/20 border-white/30' : ''}`}
              >
                Demo
              </Badge>
            </div>
            <p className="text-[11px] opacity-70 pl-6">Strategic overview & analytics</p>
          </button>
          
          <button 
            onClick={() => handleViewSwitch('/operational')}
            aria-current={isOperational ? 'page' : undefined}
            className={`w-full flex flex-col gap-1 p-3 rounded-xl text-left cursor-pointer transition-all ${
              isOperational 
                ? 'bg-brand-accent text-brand-text-primary shadow-md ring-2 ring-brand-accent/30' 
                : 'text-brand-text-secondary hover:bg-white/50 border border-transparent hover:border-brand-border'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <i className="fa-solid fa-briefcase text-base"></i>
                <span className="font-semibold text-sm">Daily Operations</span>
              </div>
              <Badge 
                variant="outline" 
                className={`text-[10px] h-5 ${isOperational ? 'bg-white/20 border-white/30' : ''}`}
              >
                Live
              </Badge>
            </div>
            <p className="text-[11px] opacity-70 pl-6">Process & review transactions</p>
          </button>
        </div>
        
        <div className="w-full h-[1px] bg-brand-border my-3"></div>
        
        {currentMenuItems.map((item) => {
          const active = matchesPath(item.path);
          return (
            <button 
              key={item.label} 
              onClick={() => handleViewSwitch(item.path)}
              className={`flex items-center gap-3 pl-4 pr-3 py-2 rounded-3xl text-sm cursor-pointer transition-colors ${
                active 
                  ? 'bg-brand-accent text-brand-text-primary font-medium' 
                  : 'text-brand-text-secondary hover:bg-white/50'
              }`}
              aria-current={active ? 'page' : undefined}
            >
              <i className={`${item.icon} w-5 text-center`}></i>
              <span>{item.label}</span>
            </button>
          );
        })}
      </nav>

      <div className="w-full h-[212px] p-4 relative bg-brand-primary rounded-2xl flex flex-col justify-end items-start gap-4 overflow-hidden mt-auto">
        <div className="w-8 h-8 p-[7px] left-[17px] top-[16px] absolute justify-center items-center inline-flex bg-brand-secondary rounded-lg">
          <i className="fa-solid fa-unlock-keyhole text-brand-primary"></i>
        </div>
        <p className="text-brand-secondary text-xs leading-4 z-10">
          {isOperational 
            ? 'Gain full access to your finances with detailed analytics and graphs.'
            : 'Unlock advanced analytics and predictive forecasting.'
          }
        </p>
        <button className="px-3.5 py-2.5 bg-brand-accent rounded-lg justify-center items-center inline-flex text-center text-brand-text-primary text-sm font-medium leading-[14px] z-10">
          {isOperational ? 'Get Pro' : 'Upgrade to Pro'}
        </button>
        <div className="w-[65px] h-[65px] left-[110px] top-[-8px] absolute opacity-20">
          <svg className="absolute w-full h-full" viewBox="0 0 65 65" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M34.6719 0L34.6719 29.3125L64.0001 29.3125C64.0001 13.1562 50.8438 0 34.6719 0Z" fill="hsl(var(--brand-accent))"></path>
            <path d="M0 1.03125V29.3125H29.3125C29.3125 13.1562 16.1562 1.03125 0 1.03125Z" fill="hsl(var(--brand-secondary))"></path>
            <path d="M0 35.6875V64H29.3125C29.3125 50.2188 16.1562 35.6875 0 35.6875Z" fill="hsl(var(--brand-accent))"></path>
            <path d="M34.6719 35.6875V64C50.8438 64 64.0001 50.2188 64.0001 35.6875H34.6719Z" fill="hsl(var(--brand-secondary))"></path>
          </svg>
        </div>
      </div>
    </aside>
  );
};